import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  Country,
  Currency,
  Pagination,
  PermissionEnum,
  Response,
  SelectItemProps,
} from '@ui/src';
import { authApi } from '@auth/hoc/store/services';
import { CurrentUser } from '@auth/hoc/types';
import { usersApi } from '@administration/hoc/user-list/store/services/users';
import { pspDetailsApi } from '@administration/hoc/settings/store/services/details';
import { userRolesApi } from '@administration/hoc/user-roles/store/services/roles';
import {
  EditRoleRequest,
  RoleDetails,
} from '@administration/hoc/user-roles/types';
import {
  PSPDetails,
  NotificationMessage,
  PaymentType,
  FFSettingsTypes,
} from './types';
import { rootComponentApi } from './service';

export interface RootComponentState {
  activePermissions?: PermissionEnum[];
  userId?: string;
  accessToken?: string;
  focused?: boolean;
  logo?: string;
  favicon?: string;
  cashCollectionEnabled?: boolean;
  cardsEnabledValue?: boolean;
  notificationChannelId?: string;
  notificationsMessages?: NotificationMessage[];
  notificationMessagesPagination?: Pagination;
  paymentTypes?: PaymentType[];
  whitelabel?: {
    data: {
      value: Record<string, string>;
    };
  };
  user?: CurrentUser;
  countriesList?: SelectItemProps[];
  countries?: Country[];
  currenciesList?: SelectItemProps[];
  currencies?: Currency[];
  baseCurrency?: Currency;
  settings?: PSPDetails[];
  notificationVisible?: {
    isVisible: boolean;
    type: string;
    loading?: boolean;
  };
  notificationText?: string;
  ffSettings?: FFSettingsTypes;
}

const initialState: RootComponentState = {
  accessToken: '',
  notificationVisible: {
    isVisible: false,
    type: '',
  },
};

export const rootComponentSlice = createSlice({
  name: 'rootComponent',
  initialState,
  reducers: {
    clearAccessToken: (state) => {
      state.accessToken = undefined;
    },
    setFocused: (state, action: PayloadAction<boolean>) => {
      state.focused = action.payload;
    },
    setNotificationChannelId: (state, action: PayloadAction<string>) => {
      state.notificationChannelId = action.payload;
    },
    setNotificationMessages: (
      state,
      action: PayloadAction<NotificationMessage[] | undefined>,
    ) => {
      state.notificationsMessages = action.payload;
    },
    setPaymentTypes: (state, action: PayloadAction<PaymentType[]>) => {
      state.paymentTypes = action.payload;
    },
    setRootSettings: (state, action: PayloadAction<PSPDetails[]>) => {
      state.settings = action.payload;
    },
    setNotificationVisible: (
      state,
      action: PayloadAction<{
        text: string;
        visible: boolean;
        type: string;
        loading?: boolean;
      }>,
    ) => {
      state.notificationVisible = {
        isVisible: action.payload.visible,
        type: action.payload.type,
        loading: action.payload.loading,
      };
      state.notificationText = action.payload.text;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      rootComponentApi.endpoints.getRootMessageToken.matchFulfilled,
      (state, action: PayloadAction<{ data: { accessToken: string } }>) => {
        state.accessToken = action.payload.data.accessToken;
      },
    );
    builder.addMatcher(
      rootComponentApi.endpoints.getFFSettings.matchFulfilled,
      (state, action: PayloadAction<{ data: FFSettingsTypes }>) => {
        state.ffSettings = action.payload.data;
      },
    );
    builder.addMatcher(
      rootComponentApi.endpoints.getNotificationChannelMessages.matchFulfilled,
      (
        state,
        action: PayloadAction<Response<NotificationMessage>> & {
          meta: any;
        },
      ) => {
        if (
          state.notificationsMessages &&
          !action.meta.arg.originalArgs.refresh
        ) {
          const nextNotifications = ([] as NotificationMessage[]).concat(
            state.notificationsMessages,
          );

          action?.payload?.data?.edges?.forEach((item) => {
            if (
              !nextNotifications.find(
                (notification) => item.node.id === notification.id,
              )
            ) {
              nextNotifications.push(item.node);
            }
          });

          state.notificationsMessages = nextNotifications;
        } else {
          state.notificationsMessages = action.payload?.data?.edges?.map(
            (item) => item.node,
          );
        }

        state.notificationMessagesPagination = action.payload?.data?.pageInfo;
      },
    );
    builder.addMatcher(
      rootComponentApi.endpoints.getPaymentTypes.matchFulfilled,
      (state, action: PayloadAction<Response<PaymentType>>) => {
        state.paymentTypes = action?.payload?.data?.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      rootComponentApi.endpoints.readAllNotifications.matchFulfilled,
      (
        state,
        action: {
          meta: {
            arg: {
              originalArgs: {
                body: {
                  messages: string[];
                };
              };
            };
          };
        },
      ) => {
        state.notificationsMessages = state.notificationsMessages?.map(
          (item) => {
            const result = { ...item };

            if (
              action?.meta?.arg?.originalArgs?.body?.messages?.find(
                (messageId) => item.id === messageId,
              )
            ) {
              result.readAt = new Date().toISOString();
            }

            return result;
          },
        );
      },
    );
    builder.addMatcher(
      rootComponentApi.endpoints.unreadAllNotifications.matchFulfilled,
      (
        state,
        action: {
          meta: {
            arg: {
              originalArgs: {
                body: {
                  messages: string[];
                };
              };
            };
          };
        },
      ) => {
        state.notificationsMessages = state.notificationsMessages?.map(
          (item) => {
            const result = { ...item };

            if (
              action?.meta?.arg?.originalArgs?.body?.messages?.find(
                (messageId) => item.id === messageId,
              )
            ) {
              result.readAt = undefined;
            }

            return result;
          },
        );
      },
    );
    builder.addMatcher(
      rootComponentApi.endpoints.getRootDictionaries.matchFulfilled,
      (
        state,
        action: PayloadAction<{
          countries: Response<Country>;
          currencies: Response<Currency>;
          baseCurrency: { data: Currency };
          settings: Response<PSPDetails>;
        }>,
      ) => {
        const { countries, currencies, baseCurrency, settings } =
          action.payload;

        if (settings) {
          const whitelabel = settings?.data?.edges?.find(
            (item) => item.node?.key === 'WHITELABEL',
          );

          const cashCollectionEnabled = settings?.data?.edges?.find(
            (item) => item.node.key === 'PAYMENTS-ORDERS-ENABLED',
          );

          const cardsEnabled = settings?.data?.edges?.find(
            (item) => item.node.key === 'CARDS',
          );

          if (cashCollectionEnabled) {
            state.cashCollectionEnabled = cashCollectionEnabled?.node?.data
              ?.value as boolean;
          }

          if (cardsEnabled) {
            state.cardsEnabledValue = cardsEnabled?.node?.data
              ?.value as boolean;
          }

          if (whitelabel) {
            state.logo = (
              whitelabel?.node?.data?.value as Record<string, string>
            )?.headerLogo;

            state.favicon = (
              whitelabel?.node?.data?.value as Record<string, string>
            )?.favicon;
          }

          state.settings = settings?.data?.edges?.map((item) => item.node);
        }

        if (countries) {
          state.countries = countries?.data?.edges?.map((item) => item.node);
          state.countriesList = countries?.data?.edges?.map((item) => ({
            name: item.node.name,
            value: item.node.id,
          }));
        }

        if (currencies) {
          state.currencies = currencies?.data?.edges?.map((item) => item.node);
          state.currenciesList = currencies?.data?.edges?.map((item) => ({
            name: item.node.description,
            value: item.node.id,
            suffix: item.node.name,
          }));
        }

        if (baseCurrency) {
          state.baseCurrency = baseCurrency.data;
        }
      },
    );
    builder.addMatcher(
      authApi.endpoints.getCurrentUser.matchFulfilled,
      (state, action: PayloadAction<{ data: any }>) => {
        state.activePermissions = action.payload.data.permissions;
        state.userId = action.payload.data.id;
        state.user = action.payload.data;

        localStorage.setItem(
          'activePermissions',
          JSON.stringify(action.payload.data.permissions),
        );
      },
    );
    builder
      .addMatcher(
        usersApi.endpoints.editPermissions.matchFulfilled,
        (
          state,
          action: {
            meta: {
              arg: {
                originalArgs: {
                  id: string;
                  body: {
                    permissions: PermissionEnum[];
                  };
                };
              };
            };
          },
        ) => {
          if (state.userId === action.meta.arg.originalArgs.id) {
            state.activePermissions =
              action.meta.arg.originalArgs.body.permissions;

            localStorage.setItem(
              'activePermissions',
              JSON.stringify(action.meta.arg.originalArgs.body.permissions),
            );
          }
        },
      )
      .addMatcher(
        userRolesApi.endpoints.editRole.matchFulfilled,
        (
          state,
          action: PayloadAction<{ data: RoleDetails }> & {
            meta: {
              arg: {
                originalArgs: {
                  body: EditRoleRequest;
                  id: string;
                };
              };
            };
          },
        ) => {
          if (
            state.user?.employeeRoleTemplateId ===
            action.meta.arg.originalArgs.id
          ) {
            state.activePermissions =
              action.meta.arg.originalArgs.body.permissions;

            localStorage.setItem(
              'activePermissions',
              JSON.stringify(action.meta.arg.originalArgs.body.permissions),
            );
          }
        },
      )
      .addMatcher(
        rootComponentApi.endpoints.getWhitelabel.matchFulfilled,
        (
          state,
          action: PayloadAction<{
            data: {
              data: {
                value: Record<string, string>;
              };
            };
          }>,
        ) => {
          state.whitelabel = action.payload.data;
        },
      )
      .addMatcher(
        pspDetailsApi.endpoints.editPSPDetails.matchFulfilled,
        (state, action: PayloadAction<{ data: PSPDetails }>) => {
          if (action?.payload?.data?.key === 'PAYMENTS-ORDERS-ENABLED') {
            state.cashCollectionEnabled = action.payload.data?.data
              ?.value as boolean;
          }
        },
      );
  },
});

export const {
  setFocused,
  setNotificationChannelId,
  setNotificationMessages,
  setPaymentTypes,
  setRootSettings,
  clearAccessToken,
  setNotificationVisible,
} = rootComponentSlice.actions;

export default rootComponentSlice.reducer;
